<script setup lang="ts">
import VPageContainer from '@shared/layout/VPageContainer/VPageContainer.vue';
import VContentBox from '@shared/component/VContentBox/VContentBox.vue';
import FileDownload from '@shared/component/FileDownload/FileDownload.vue';
import VHeading from '@shared/typography/VHeading/VHeading.vue';
import VInfoBox from '@shared/component/VInfoBox/VInfoBox.vue';
import VSkeleton from '@shared/component/VSkeleton/VSkeleton.vue';
import VSizeContainer from '@shared/layout/VSizeContainer/VSizeContainer.vue';
import { useForm } from 'vee-validate';
import { computed, toRefs, watch } from 'vue';
import { useRouter } from 'vue-router';
import { toTypedSchema } from '@vee-validate/zod';
import { z } from 'zod';
import { useUserProfile } from '@auth/useProfile';
import { useGebruikerGet } from '@services/crm-internal/orval-api/gebruiker-api/gebruiker-api';
import {
  useSamenwerkingsovereenkomstGet,
  useSamenwerkingsovereenkomstOnderteken,
} from '@services/documenten-public/orval-api/samenwerkingsovereenkomst/samenwerkingsovereenkomst';
import { createErrorMessage } from '@errors/helpers';
import { useErrorHandler, useWatchErrorHandler } from '@errors/useErrorHandler';
import FieldGroup from '@shared/form/FieldRowV2/FieldGroup.vue';
import FieldRowV2 from '@shared/form/FieldRowV2/FieldRowV2.vue';
import VDatePicker from '@shared/form/VDatePicker/VDatePicker.vue';
import StringField from '@shared/form/StringField/StringField.vue';
import { useQuery } from '@tanstack/vue-query';
import apiService from '@services/base/apiService';
import { useNotification } from '@shared/plugin/prime-vue/useNotification';
import { useClaims } from '@auth/useClaims';
import { type SwoForm } from './types';
import SwoFooter from './SwoFooter.vue';
import SwoAkkoord from './SwoAkkoord.vue';

const props = withDefaults(defineProps<{ beurs?: boolean }>(), { beurs: false });
const { beurs } = toRefs(props);
const router = useRouter();
const { userProfile } = useUserProfile();
const notification = useNotification();
const claims = useClaims();
const { handleError } = useErrorHandler();
const {
  data: swo,
  isPending: swoLoading,
  error,
  isError,
} = useSamenwerkingsovereenkomstGet(
  computed(() => ({ beurs: beurs.value })),
  { query: { select: (r) => r.result } },
);

const { data: gebruiker } = useGebruikerGet(
  computed(() => userProfile.value?.id),
  {
    query: {
      select: (r) => r.result,
      enabled: computed(
        () =>
          swo.value?.status === 'KlaarVoorOndertekening' &&
          !!userProfile.value?.id &&
          !claims.value?.isTemporaryUser,
      ),
    },
  },
);

function onSuccess() {
  router.push({ name: 'samenwerkingsovereenkomstBedankt' });
}

const {
  isPending,
  data,
  error: swoGeneratieError,
} = useQuery({
  queryKey: ['samenwerkingsovereenkomst', beurs],
  queryFn: () =>
    apiService.apiClient.get(
      `content/beheer/intern/tussenpersonen/details/dossier/ajax/genereersamenwerkingsovk/?nietopslaan=1&beurs=${Number(beurs.value)}`,
    ),
  enabled: computed(() => swo.value?.status === 'KlaarVoorOndertekening'),
});

watch(data, (res) => {
  if (res?.data?.foutmelding?.length)
    notification.error(res?.data?.foutmelding ?? 'Onbekende fout opgetreden');
});

useWatchErrorHandler(swoGeneratieError);

const { mutate, isPending: ondertekenenLoading } = useSamenwerkingsovereenkomstOnderteken({
  mutation: {
    onSuccess,
    onError: (err: any) => handleError(err),
  },
});

const disabled = computed(
  () => swo.value?.status === 'ReedsOndertekend' || ondertekenenLoading.value || isError.value,
);

const validationSchema = toTypedSchema(
  z.object({
    akkoord: z.boolean().superRefine((val, ctx) => {
      if (!val) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Akkoord is verplicht',
        });
      }
    }),
    datum: z.date(),
    plaats: z.string({ required_error: 'Plaats is verplicht' }).min(1),
    voorletters: z.string({ required_error: 'Voorletters zijn verplicht' }).min(1),
    tussenvoegsel: z.string().optional(),
    achternaam: z.string({ required_error: 'Achternaam is verplicht' }).min(1),
    functie: z.string({ required_error: 'Functie is verplicht' }).min(1),
  }),
);

const { defineField, handleSubmit, errors, resetForm } = useForm<SwoForm>({
  validationSchema,
  initialValues: {
    datum: new Date(),
    akkoord: false,
  },
});

watch([gebruiker], () => {
  resetForm({
    values: {
      functie: userProfile?.value?.functie ?? undefined,
      voorletters: gebruiker.value?.voorletters ?? undefined,
      tussenvoegsel: gebruiker.value?.tussenvoegsels ?? undefined,
      achternaam: gebruiker.value?.naam ?? undefined,
    },
  });
});

const [akkoord] = defineField('akkoord');
const [datum] = defineField('datum');
const [plaats] = defineField('plaats');
const [voorletters] = defineField('voorletters');
const [tussenvoegsel] = defineField('tussenvoegsel');
const [achternaam] = defineField('achternaam');
const [functie] = defineField('functie');

const submit = handleSubmit(() => {
  mutate({
    data: {
      voorletters: voorletters.value,
      tussenvoegsel: tussenvoegsel.value,
      achternaam: achternaam.value,
      functie: functie.value,
      plaats: plaats.value,
      beurs: beurs.value,
    },
  });
});
</script>
<template>
  <div>
    <VPageContainer title="Samenwerkingsovereenkomst ondertekenen">
      <VInfoBox class="mb-4" type="danger" v-if="error">
        {{
          createErrorMessage(error, {
            onValidationError:
              'De machtiging om deze samenwerkingsovereenkomst te ondertekenen is toegekend aan een andere gebruiker.',
            onNotFoundError:
              'Er staat momenteel geen samenwerkingsovereenkomst klaar om te ondertekenen.',
          })
        }}
      </VInfoBox>
      <VInfoBox class="mb-4" type="info" v-else-if="swo?.status === 'ReedsOndertekend'">
        Deze samenwerkingsovereenkomst is reeds ondertekend.
      </VInfoBox>
      <VContentBox no-padding :margin-bottom="0">
        <VSizeContainer size="sm" class="my-6">
          <VSkeleton v-if="swoLoading" :count="12" height="lg" />
          <div v-else class="flex flex-col gap-2 p-4">
            <VHeading :level="2" appearance="m">Welkom</VHeading>
            <p class="text-sm text-grey">
              Je bent op deze pagina terechtgekomen om onze samenwerkingsovereenkomst te
              ondertekenen.
            </p>
            <br />
            <VHeading :level="2" appearance="m">Document</VHeading>
            <p class="text-sm text-grey">
              Hieronder vind je de samenwerkingsovereenkomst. Neem de tijd om het document rustig
              door te lezen en te controleren.
            </p>
            <p class="text-sm text-grey">
              Zodra je akkoord bent met de inhoud, kun je de overeenkomst eenvoudig digitaal
              ondertekenen.
            </p>
            <FileDownload
              class="mt-4"
              :error-message="
                swo?.status === 'KlaarVoorOndertekening'
                  ? undefined
                  : `Er is geen document gegenereerd.`
              "
              :loading="isPending"
              download
              :description="`${props.beurs ? 'Samenwerkingsovereenkomst_beurs.pdf' : 'Samenwerkingsovereenkomst.pdf'}`"
              :file-url="data?.data.DownloadURL"
            />
            <br />
            <VHeading :level="2" appearance="m">Bevestiging</VHeading>
            <p class="text-sm text-grey">
              Als je akkoord gaat, vul dan je gegevens in en bevestig de ondertekening.
            </p>
            <p class="text-sm text-grey">
              Na ondertekening ontvang je een bevestiging en een kopie van de overeenkomst voor je
              administratie.
            </p>
            <br />
            <FieldGroup>
              <FieldRowV2 label="Datum en plaats" :errors="[errors.plaats]">
                <VDatePicker class="basis-40" disabled v-model="datum" />
                <StringField
                  :has-error="!!errors.plaats"
                  :disabled
                  class="basis-40"
                  placeholder="Plaats"
                  v-model="plaats"
                />
              </FieldRowV2>
              <FieldRowV2
                label="Naam"
                :errors="[errors.voorletters, errors.tussenvoegsel, errors.achternaam]"
              >
                <StringField
                  :disabled
                  class="basis-40"
                  placeholder="Voorletters"
                  v-model="voorletters"
                />
                <StringField
                  :disabled
                  class="basis-40"
                  placeholder="Tussenvoegsel"
                  v-model="tussenvoegsel"
                />
                <StringField
                  :disabled
                  class="basis-60"
                  placeholder="Achternaam"
                  v-model="achternaam"
                />
              </FieldRowV2>
              <FieldRowV2 label-for="functie" label="Functie" :errors="[errors.functie]">
                <StringField
                  id="functie"
                  :has-error="!!errors.functie"
                  placeholder="Directeur / eigenaar"
                  v-model="functie"
                  :disabled
                />
              </FieldRowV2>
            </FieldGroup>
          </div>
        </VSizeContainer>
        <SwoAkkoord :disabled v-model="akkoord" :errors="[errors.akkoord]" />
      </VContentBox>
    </VPageContainer>
    <SwoFooter :disabled :loading="ondertekenenLoading" @submit="submit()" />
  </div>
</template>
